import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const Trademark = () => {
  const data = {
    title: 'Trademark Registration',
    description: 'Protect your brand name, logo, and identity with trademark registration. Secure exclusive rights to use your mark across India for 10 years, renewable indefinitely.',
    keywords: 'trademark registration, brand protection, logo registration, trademark India, TM registration, intellectual property, brand name registration',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          A Trademark is a unique symbol, word, phrase, logo, design, or combination that identifies and distinguishes 
          the goods or services of one business from another. Trademark registration provides legal protection and 
          exclusive rights to use the mark throughout India. Once registered, you can prevent others from using similar 
          marks that could confuse customers.
        </p>
        <p>
          <strong>Why Register Your Trademark?</strong> In today's competitive market, your brand name and logo are 
          invaluable assets. Without registration, anyone can copy your brand identity, dilute your reputation, and 
          steal your customers. Trademark registration gives you legal recourse against infringers, the right to use 
          the ® symbol, and enhances brand value for investors and buyers.
        </p>
        <p>
          <strong>Legal Framework:</strong> Trademarks in India are governed by the Trade Marks Act, 1999 and administered 
          by the Controller General of Patents, Designs and Trade Marks under the Ministry of Commerce. Registration is 
          valid for 10 years and can be renewed indefinitely. India follows the "first to file" system, so early registration 
          is crucial.
        </p>
        <p>
          <strong>Types of Trademarks:</strong> You can register word marks (brand name), device marks (logo), combination 
          marks (name + logo), shape marks (unique product shape), sound marks (jingles), and color marks (specific color 
          combinations). Each type offers different levels of protection based on your branding strategy.
        </p>
      </div>
    ),
    
    benefits: [
      'Legal protection and exclusive rights to use your mark across India',
      'Right to use ® symbol – indicates registered trademark',
      'Prevents competitors from using similar marks that confuse customers',
      'Legal recourse against infringers with damages and injunctions',
      'Increases brand value and business valuation',
      'Protects your investment in brand building and marketing',
      'Required for franchise and licensing opportunities',
      'Valid for 10 years, renewable indefinitely',
      'Can be licensed or sold as a business asset',
      'Easier to take action against counterfeit products',
      'Protection on e-commerce platforms against copycats',
      'International registration possible through Madrid Protocol',
    ],
    
    documents: [
      'Applicant\'s identity proof: PAN Card, Aadhaar, or Passport',
      'Address proof of applicant (individual/company)',
      'Logo/wordmark to be registered (in JPEG format, max 8 MB)',
      'List of goods/services with Nice Classification (class 1-45)',
      'Business incorporation certificate (if company/LLP)',
      'Partnership deed (if partnership firm)',
      'Power of Attorney (if filing through agent/attorney)',
      'Proof of prior use with date (invoices, advertisements) – optional',
      'MSME/Startup certificate (for fee reduction) – optional',
      'Priority document (if claiming convention priority) – optional',
    ],
    
    process: [
      'Trademark search to check availability and similar marks – 1-2 days',
      'Selection of appropriate Nice Classification (goods/services classes)',
      'Application drafting with proper description and specifications',
      'Filing application on IP India portal with prescribed fee',
      'Examination by Trademark Registry – 1-3 months',
      'Response to examination report/objections (if any) – 30 days',
      'Publication in Trademark Journal for opposition – 4 months',
      'Opposition proceedings (if opposed by third party)',
      'Registration certificate issuance upon successful completion',
      'Annual renewal reminders and compliance support',
    ],
    
    timeline: '6-8 Months (if no objections/oppositions)',
    pricing: '₹6,999 + Govt Fees',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>Government Fees (Per Class):</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Individual/Startup/Small Enterprise: ₹4,500</li>
          <li>Other entities (companies, LLPs): ₹9,000</li>
          <li>Each additional class: Same fee applies</li>
          <li>Renewal fee (every 10 years): Same as registration fee</li>
        </ul>
        <p className="mt-3"><strong>TM vs ® Symbol:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li><strong>TM (™):</strong> Can be used immediately after filing application</li>
          <li><strong>® (Registered):</strong> Can only be used after registration is granted</li>
          <li>Using ® without registration is a criminal offense</li>
        </ul>
        <p className="mt-3"><strong>Nice Classification (Popular Classes):</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Class 9: Software, apps, electronics</li>
          <li>Class 25: Clothing, footwear</li>
          <li>Class 35: Advertising, business services, retail</li>
          <li>Class 41: Education, entertainment services</li>
          <li>Class 42: IT services, website design</li>
          <li>Class 43: Restaurant, hotel services</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'How long does trademark registration take?',
        answer: 'Trademark registration typically takes 6-8 months if there are no objections or oppositions. If objections are raised by the examiner, additional 2-3 months. If opposed by third party, it can take 1-2 years to resolve.',
      },
      {
        question: 'Can I use the TM symbol before registration?',
        answer: 'Yes, you can use TM (™) symbol immediately after filing the application to indicate that you have applied for registration and claim rights over the mark. The ® symbol can only be used after registration is granted.',
      },
      {
        question: 'What is the validity of trademark registration?',
        answer: 'Trademark registration is valid for 10 years from the date of application filing. It can be renewed indefinitely for successive 10-year periods by paying the renewal fee. Renewal application should be filed before expiry.',
      },
      {
        question: 'Can I register a trademark in multiple classes?',
        answer: 'Yes, you can file one application covering multiple classes of goods/services. However, government fee is charged per class. Multi-class application is common for businesses offering diverse products/services under one brand.',
      },
      {
        question: 'What if someone is already using a similar mark?',
        answer: 'If a similar mark is already registered or in use, your application may be rejected or opposed. We conduct a thorough trademark search before filing to identify potential conflicts and advise on alternatives or modifications.',
      },
      {
        question: 'Is trademark registration valid worldwide?',
        answer: 'Indian trademark registration is valid only in India. For international protection, you can file through Madrid Protocol in 130+ countries or file separately in each country. We can assist with international trademark filing.',
      },
      {
        question: 'What is trademark opposition?',
        answer: 'After your trademark is published in the Trademark Journal, any person can oppose the registration within 4 months if they believe it infringes their rights. Opposition proceedings involve evidence and hearings before the Registry.',
      },
      {
        question: 'Can I transfer my trademark to someone else?',
        answer: 'Yes, registered trademark can be assigned (sold) or licensed to others. Assignment transfers ownership permanently, while licensing grants usage rights for specific period/purpose. Both require recording with the Trademark Registry.',
      },
    ],
  };

  return <ServiceTemplate {...data} />;
};

export default Trademark;
